/**
 * SidebarContext — shared collapse state for the entire sidebar system.
 * Wrap your authenticated layout with <SidebarProvider>.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const SidebarContext = createContext(null);
const STORAGE_KEY = "clozdl.sidebar.collapsed";

function getInitialCollapseState() {
  if (typeof window === "undefined") return false;

  return localStorage.getItem(STORAGE_KEY) === "true";
}

export function SidebarProvider({ children }) {
  const [collapsed, setCollapsedState] = useState(getInitialCollapseState);

  const setCollapsed = useCallback((value) => {
    setCollapsedState((previous) => {
      if (typeof value === "function") return value(previous);

      return value;
    });
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsedState((previous) => !previous);
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    function handleShortcut(event) {
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.key.toLowerCase() !== "e") return;

      event.preventDefault();
      toggleCollapsed();
    }

    window.addEventListener("keydown", handleShortcut);

    return () => window.removeEventListener("keydown", handleShortcut);
  }, [toggleCollapsed]);

  const value = useMemo(
    () => ({
      collapsed,
      setCollapsed,
      toggleCollapsed,
    }),
    [collapsed, setCollapsed, toggleCollapsed]
  );

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error("useSidebar must be used inside <SidebarProvider>");
  return ctx;
}


